import { useCallback, useMemo, useState } from 'react';
import type { BulkItem, DocumentSearchRow } from '../../types/documentsSearch';

/**
 * Selection state for the Search Results > Documents grid.
 *
 * Keyed by `doc_key` — `{fileinfo_recno}_{ref}_{source}`, unique per document
 * across the whole result set. It is a STRING, not a recno: the grid unions
 * three document sources whose map tables have independent recno sequences, so
 * a numeric id would collide across them. The whole row is kept, not just the
 * key, so `items` can build the composite the bulk endpoints validate ownership
 * against without re-looking-up rows that a filter change may have removed from
 * the grid.
 *
 * `checkAll` is a TOGGLE scoped to the rows it is given (the currently
 * displayed/filtered ones): if all of them are already selected it unchecks
 * exactly those, otherwise it adds them. Rows selected under a previous filter
 * survive both directions, so a user can build a selection across several
 * filter passes and still email it in one go. `checkAll([])` does nothing — an
 * empty grid (mid-fetch, or a filter that matches nothing) must not silently
 * wipe a selection.
 */
export interface DocSelection {
  selected: Map<string, DocumentSearchRow>;
  count: number;
  isSelected: (docKey: string) => boolean;
  toggle: (row: DocumentSearchRow) => void;
  checkAll: (rows: DocumentSearchRow[]) => void;
  clear: () => void;
  items: BulkItem[];
}

export function useDocSelection(): DocSelection {
  const [selected, setSelected] = useState<Map<string, DocumentSearchRow>>(
    () => new Map(),
  );

  const isSelected = useCallback(
    (docKey: string) => selected.has(docKey),
    [selected],
  );

  const toggle = useCallback((row: DocumentSearchRow) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(row.doc_key)) next.delete(row.doc_key);
      else next.set(row.doc_key, row);
      return next;
    });
  }, []);

  const checkAll = useCallback((rows: DocumentSearchRow[]) => {
    if (rows.length === 0) return;
    setSelected((prev) => {
      const next = new Map(prev);
      const allOn = rows.every((r) => prev.has(r.doc_key));
      for (const r of rows) {
        if (allOn) next.delete(r.doc_key);
        else next.set(r.doc_key, r);
      }
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    // Preserve identity when already empty so effects keyed on `selected`
    // don't re-run on every search change.
    setSelected((prev) => (prev.size === 0 ? prev : new Map()));
  }, []);

  const items = useMemo<BulkItem[]>(
    () => Array.from(selected.values(), (r) => ({
      source: r.source, ref: r.ref, fileinfo_recno: r.fileinfo_recno,
      pronumber: r.pronumber,
    })),
    [selected],
  );

  return { selected, count: selected.size, isSelected, toggle, checkAll, clear, items };
}

export default useDocSelection;
